import React, { type CSSProperties } from "react";

import clsx from "clsx";

import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { Checkbox } from "@shared/checkbox";

import { type ListItem } from "./types";

interface Props {
  item: ListItem;
  checkboxDisabled: boolean;
  isExpanded: boolean;
  style: CSSProperties;
  onChange(values: string[]): void;
  onToggle(value: string): void;
}

export const BreakdownFilterItem = ({
  item,
  checkboxDisabled,
  isExpanded,
  style,
  onChange,
  onToggle,
}: Props) => {
  const { designElementManager } = useDesignElementManager();

  const label = item.label === "" ? "(not set)" : item.label;
  const icon =
    item.breakdown === "Platform (Northbeam)"
      ? designElementManager
          .getDesignElement(label)
          .getComponent({ size: "22px" })
      : null;

  return (
    <div
      className="flex items-center h-8 absolute top-0 left-0 gap-2"
      style={{ ...style, marginLeft: `${item.level * 32}px` }}
    >
      {item.isLeaf ? (
        <label className="flex items-center gap-1 m-0 cursor-pointer">
          <Checkbox
            value={item.selected}
            onChange={() => onChange(item.breadcrumbs)}
            isDisabled={checkboxDisabled && !item.selected}
          />
          <span className="flex items-center gap-2">
            {icon}
            <span className="flex flex-col gap-0.5">
              <span className="leading-3">{label}</span>
              {item.maxAllowedLevel !== "campaign" && (
                <span className="text-xxs text-cosmic">
                  Available only on{" "}
                  {item.maxAllowedLevel === "ad" ? "Ad" : "Ad and Adset"} level
                </span>
              )}
            </span>
          </span>
        </label>
      ) : (
        <>
          <button
            className={clsx(
              "w-6 h-6 shrink-0 self-center text-cosmic text-[0.65rem] rounded-lg",
              "hover:bg-cosmic-3",
            )}
            onClick={() => onToggle(item.key)}
          >
            <i
              className={clsx("fa-regular fa-sharp", {
                "fa-chevron-down": !isExpanded,
                "fa-chevron-up": isExpanded,
              })}
            />
          </button>
          {icon}
          <span>{label}</span>
          {item.maxAllowedLevel !== "campaign" && (
            <span className="text-xxs text-cosmic mt-0.5">
              {item.maxAllowedLevel === "ad" ? "Ad" : "Ad, Adset"}
            </span>
          )}
          {item.maxAllowedLevel !== "campaign" && item.selectedCount > 0 && (
            <span className="text-cosmic">&bull;</span>
          )}
          {item.selectedCount > 0 && (
            <span className="text-xxs text-cosmic mt-0.5">
              {item.selectedCount} selected
            </span>
          )}
        </>
      )}
    </div>
  );
};
